import { App } from 'antd';
import type { MenuProps } from 'antd/lib';
import { FilePublicPasswordForm } from '../../components/Forms/FilePublicPasswordForm';
import type { ResultStatusType } from 'antd/lib/result';
import type File from 'interfaces/File';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AiDocumentChat from '../../components/Ai/AiDocumentChat';
import AiSummary from '../../components/Ai/AiSummary';
import { Icon } from '../../components/Core/Icon';
import { AiClassificationStatusEnum } from '../../enums/AiClassificationStatusEnum';
import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { getApiErrorCode, getApiErrorField } from '../../utils/apiErrorCode';
import { getAntivirusInfectedMessage } from '../../utils/antivirus';
import { mapPublicLinkHttpError } from '../../utils/publicLinkErrors';
import {
  getPublicDownloadFilename,
  getResponseHeader,
  triggerBlobDownload,
} from '../../lib/fileDownload';
import { getHashFromBytes } from '../../utils/utils';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { FileSalesStatusEnum } from '../../enums/FileSalesStatusEnum';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import type FileActionContext from '../../interfaces/FileActionContext';
import type FileWithToken from '../../interfaces/FileWithToken';
import type ViewerFile from '../../interfaces/ViewerFile';
import { useRoutePageTitle } from '../../lib/documentTitle';
import { fileActions } from '../../lib/fileActions';
import { getOfficePath } from '../../lib/officeRoutePath';
import { FileService } from '../../services/fileApiService';
import { FunctionService } from '../../services/functionService';
import { PublicFileService } from '../../services/publicFileApiService';
import { useAppStore } from '../../store/store';
import { decryptBlob as decryptStoredBlob } from '../../utils/blobDecryption';
import { handleFilePasswordError } from '../../utils/filePasswordErrors';
import { hasFileAccessScope } from '../../utils/jwt';
import { hasFileLimitation } from '../../utils/utils';
import { authService } from '../../services/authService';
import { UnsavedChangesScopeEnum } from '../../enums/UnsavedChangesScopeEnum';
import { useAuth } from '../useAuth';
import { useFileDownload } from '../useFileDownload';
import { useRequestLeave } from '../useRequestLeave';
import { useSnapshotDirtyState } from '../useSnapshotDirtyState';
import { useFilesActions } from '../useFilesActions';
import { useRightPanel } from '../useRightPanel';
import { SpecialFolderTypeEnum } from '../../enums/SpecialFolderTypeEnum';
import { isViewerEmbedSession } from '../../utils/viewerWebviewAuth';
import { normalizeViewerFile } from './normalizeViewerFile';
import { canStreamVideo } from './videoStreaming';
import { getJwtExpiration } from '../../utils/jwt/jwt';

/**
 * Placeholder blob for videos played via `<video src=streamUrl>`: the viewer components expect a
 * `decryptedFileBlob`, but a streamed video is never downloaded whole. Shared instance so a token
 * refresh does not re-trigger blob-dependent effects.
 */
const EMPTY_STREAM_BLOB = new Blob([]);
// Error states for the viewer

enum ViewerErrorType {
  IsFolder = 'IsFolder',
  FetchFilePermissionsFailed = 'FetchFilePermissionsFailed',
  BlobDecryptFailed = 'BlobDecryptFailed',
  OfficeNoPreview = 'OfficeNoPreview',
  BlobFetchFailed = 'BlobFetchFailed',
  NoPermissions = 'NoPermissions',
  FileSizeViewerOver = 'FileSizeViewerOver',
  AntivirusInfected = 'AntivirusInfected',
  PublicLinkUnavailable = 'PublicLinkUnavailable',
  PublicLinkExpired = 'PublicLinkExpired',
  PublicItemUnavailable = 'PublicItemUnavailable',
}

// Error details for viewer errors
type ViewerError = {
  status: ResultStatusType;
  code: ViewerErrorType;
  title: string;
  description: string;
  canReloadPage?: boolean; // Hides reload button if false
  canDownload?: boolean; // Hides download button if false
};

// Type for download action in viewer
type ViewerDownloadAction = {
  key: FileMenuActionEnum;
  label: ReactNode;
  labelText: string;
  icon?: ReactNode;
};

// Service instances
const fileService = new FileService();
const publicFileService = new PublicFileService();

// File types supported in viewer
const PREVIEWABLE_FILE_TYPES: FileTypeEnum[] = [
  FileTypeEnum.Text,
  FileTypeEnum.Code,
  FileTypeEnum.Image,
  FileTypeEnum.Audio,
  FileTypeEnum.Video,
  FileTypeEnum.Pdf,
  FileTypeEnum.Word,
  FileTypeEnum.Excel,
  FileTypeEnum.Powerpoint,
  FileTypeEnum.Wopitest,
  FileTypeEnum.Secret,
  FileTypeEnum.Note,
  FileTypeEnum.Eml,
  FileTypeEnum.Ics,
  FileTypeEnum.Vcf,
  FileTypeEnum.Msg,
];

// Checks if fileType is supported by preview
function isSupportedFileType(fileType: FileTypeEnum | undefined): boolean {
  if (fileType == null) return false;
  return PREVIEWABLE_FILE_TYPES.includes(fileType);
}

/** Maps `FILE_SIZE_VIEWER_OVER` payload from error.response.value to FileWithToken (no extra fetch). */
function mapViewerOverValueToFileWithToken(value: unknown): FileWithToken | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<FileWithToken>;
  if (v.id == null || typeof v.token !== 'string') return null;
  return {
    id: Number(v.id),
    cufi: String(v.cufi ?? ''),
    fileName: String(v.fileName ?? ''),
    fileExtension: String(v.fileExtension ?? ''),
    expirationAt: Number(v.expirationAt ?? 0),
    token: v.token,
    wTkn: String(v.wTkn ?? ''),
    fileSize: Number(v.fileSize ?? 0),
    isReadOnly: Boolean(v.isReadOnly),
    canDownload: Boolean(v.canDownload),
    fileType: (v.fileType as FileTypeEnum) ?? FileTypeEnum.Unknown,
    usePost: Boolean(v.usePost),
    fileLimitations: Array.isArray(v.fileLimitations)
      ? (v.fileLimitations as FileWithToken['fileLimitations'])
      : [],
    watermark: Boolean(v.watermark),
  };
}

export type UseViewerOptions = {
  /** Override route param — used when Viewer is embedded in the bottom drawer. */
  fileId?: string | null;
};

/**
 * Custom React hook for file viewing state and actions.
 * Manages loading, permissions, decryption, error states, and editing.
 */
function useViewer(options: UseViewerOptions = {}) {
  // Routing, search, translations, modal/context
  const navigate = useNavigate();
  const { fileId: fileIdFromParams } = useParams<{ fileId: string }>();
  const fileId =
    options.fileId != null && options.fileId !== '' ? String(options.fileId) : fileIdFromParams;
  const {
    openRightPanel,
    closeRightPanel,
    setRightPanelContent,
    setRightPanelKind,
    isRightPanelOpen,
  } = useRightPanel();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { modal } = App.useApp();

  // Public viewing (token for anonymous shared file)
  const publicFileToken = searchParams.get('publicFileToken') || undefined;

  // External access with acToken: hide HTML preview toggle and force preview-only (handled in ViewerTextEditor)
  const hasAcToken = searchParams.get('acToken') !== null;

  // File and loading/error state
  const [file, setFile] = useState<FileWithToken>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ViewerError | null>(null);

  // Internal refs for one-time triggers and password fetch handling
  const lastFetchedFileIdRef = useRef<string | undefined>(undefined); // Refetches when fileId changes (in-place note switch)
  const shareModalOpenedRef = useRef(false); // Avoids duplicate share modal when reopening
  const viewerCausedPendingRef = useRef(false); // Remembers if viewer initiated a pending password flow
  const hadPendingRequestRef = useRef(false); // Detects transition out of password pending

  // Store function for marking which component triggered password pending state
  const setPendingFilePasswordSource = useAppStore((s) => s.setPendingFilePasswordSource);
  // Public file password cache (60 min TTL) so returning to viewer with same token skips password modal
  const getPublicFilePassword = useAppStore((s) => s.getPublicFilePassword);
  const setPublicFilePassword = useAppStore((s) => s.setPublicFilePassword);

  // Blob/decryption state
  const [decryptedFileBlob, setDecryptedFileBlob] = useState<Blob | null>(null);
  const [pdfEncryptedBytes, setPdfEncryptedBytes] = useState<Uint8Array | null>(null);
  const [contentRangeLength, setContentRangeLength] = useState<number | undefined>(undefined);

  // Edit mode state and refs for editors
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSignMode, setIsSignMode] = useState(false);
  const pdfSignRef = useRef<{ save: () => Promise<void> } | null>(null);
  const requestLeave = useRequestLeave();

  // Notes auto-save (debounce + flush on switch), so they must NOT arm the navigation blocker —
  // otherwise switching between notes would pop the unsaved-changes prompt.
  const guardEnabled =
    (isInEditMode || isSignMode) && isDirty && file?.fileType !== FileTypeEnum.Note;
  useSnapshotDirtyState({
    enabled: guardEnabled,
    isDirty: guardEnabled,
    onDiscard: () => {
      setIsDirty(false);
      setIsInEditMode(false);
      setIsSignMode(false);
      setImageBlobOverride(null);
      // Keep pdfBlobOverride: replace already burned stamps into that blob.
    },
    scope: UnsavedChangesScopeEnum.Page,
  });
  const [imageBlobOverride, setImageBlobOverride] = useState<Blob | null>(null);
  const [pdfBlobOverride, setPdfBlobOverride] = useState<Blob | null>(null);
  /** Bumps ViewerPdf remount so signed blob + page restore apply after replace. */
  const [pdfViewEpoch, setPdfViewEpoch] = useState(0);
  /** 1-based page for post-load goToPage restore (RPProvider initialPage alone is racy). */
  const [pdfInitialPage, setPdfInitialPage] = useState<number | undefined>();
  /** Within-page scroll for post-load restore (1-page PDF: page index alone is useless). */
  const [pdfInitialScroll, setPdfInitialScroll] = useState<
    { scrollTop: number; scrollLeft: number } | undefined
  >();
  const textViewerRef = useRef<any>(null);
  const imageEditorRef = useRef<any>(null);

  const [isHtmlPreviewEnabled, setIsHtmlPreviewEnabled] = useState(false);

  // Persisted "note context": stays true while switching between notes in-place (during which
  // `file` is briefly undefined), so the viewer can keep the note rail mounted and only spin the
  // content pane instead of replacing the whole view with the full-page loader.
  const [isNoteContext, setIsNoteContext] = useState(false);
  useEffect(() => {
    if (file?.fileType === FileTypeEnum.Note) setIsNoteContext(true);
    else if (file?.fileType) setIsNoteContext(false);
  }, [file?.fileType]);

  // UI helpers: notifications, messages
  const { notification, message } = App.useApp();

  // Service/auth
  const functionServiceRef = useRef<FunctionService>(new FunctionService()); // Avoid unintentional re-creation
  const functionService = functionServiceRef.current;
  const { handleShareFiles, handleDownloadEncryptedFile } = useFilesActions({});
  const { handleDownloadFiles } = useFileDownload();
  const { authenticatedUser, isAdminOrContributor, isReader, isInFileRoute, isLoggedIn } =
    useAuth();
  const customTokens = useAppStore((s) => s.customTokens);

  // Derived expiration date for access-limited files
  const fileAccessExpiration = useMemo(() => {
    if (file?.expirationAt && file?.expirationAt > 0) {
      const expiryTicks = file.expirationAt;
      if (expiryTicks > 0) {
        const ticksToMicrotime = expiryTicks / 10000;
        const epochMicrotimeDiff = Math.abs(new Date(0, 0, 1).setFullYear(1));
        return new Date(ticksToMicrotime - epochMicrotimeDiff).toLocaleDateString();
      }
    }
    return null;
  }, [file]);

  // Decrypts an encrypted file blob (AES CBC; WASM-accelerated)
  const decryptBlob = useCallback(
    async (blob: Blob) => {
      try {
        const mimeType =
          file?.fileType != null &&
          [
            FileTypeEnum.Pdf,
            FileTypeEnum.Word,
            FileTypeEnum.Excel,
            FileTypeEnum.Powerpoint,
          ].includes(file.fileType)
            ? 'application/pdf'
            : 'application/octet-stream';

        return await decryptStoredBlob(blob, mimeType);
      } catch (e) {
        setError({
          status: 500,
          code: ViewerErrorType.BlobDecryptFailed,
          title: t('viewer.error.decryptFailed.title'),
          description: t('viewer.error.decryptFailed.description'),
        });
        throw e;
      }
    },
    [file?.fileType, t],
  );

  // Global AbortController for file blob fetch (not all use cases wire this properly)
  const abort = new AbortController();

  // Is the file a Microsoft Office document?
  const isOfficeFile = useMemo(() => {
    return (
      file?.fileType === FileTypeEnum.Word ||
      file?.fileType === FileTypeEnum.Excel ||
      file?.fileType === FileTypeEnum.Powerpoint
    );
  }, [file?.fileType]);

  // Loads and decrypts file blob; shows progress, handles Office preview no-support
  const fetchAndDecryptBlob = useCallback(async () => {
    if (!file) return;
    let blob: Blob | null = null;
    try {
      setIsLoading(true);

      // Get encrypted blob using token
      blob = await functionService.getFileBlob(
        file.token,
        file.fileSize,
        (progress) => {
          // Show progress bar to user
          const dataProgress = document.querySelector('.meter > span') as HTMLElement;
          const progressText = document.querySelector('#fg-loader-text');
          const percentage = Math.round(progress.percent || 0);
          if (dataProgress && percentage <= 100 && progressText) {
            dataProgress.style.width = percentage + '%';
            progressText.textContent = `Loading (${percentage}%)`;
          }
        },
        abort.signal,
      );
    } catch (err: any) {
      setIsLoading(false);
      // Show clean Office-type preview not-supported error if needed
      const isOffice =
        file?.fileType === FileTypeEnum.Word ||
        file?.fileType === FileTypeEnum.Excel ||
        file?.fileType === FileTypeEnum.Powerpoint;
      if (err?.status === 422 && isOffice) {
        setError({
          status: 404,
          code: ViewerErrorType.OfficeNoPreview,
          title: t('viewer.error.officeNoPreview.title'),
          description: t('viewer.error.officeNoPreview.description'),
        });
        return;
      }
      throw err;
    }

    if (!blob) {
      setError({
        status: 500,
        code: ViewerErrorType.BlobFetchFailed,
        title: t('viewer.error.blobFetchFailed.title'),
        description: t('viewer.error.blobFetchFailed.description'),
      });
      return;
    }

    // Main decryption flow
    const { decrypted, encryptedBytes } = await decryptBlob(blob);

    setDecryptedFileBlob(decrypted);
    setContentRangeLength(encryptedBytes.length);
    setPdfEncryptedBytes(
      file?.fileType &&
        [FileTypeEnum.Pdf, FileTypeEnum.Word, FileTypeEnum.Excel, FileTypeEnum.Powerpoint].includes(
          file.fileType,
        )
        ? encryptedBytes
        : null,
    );
  }, [file, t, decryptBlob, file?.fileType]);

  // On file or fileType change, decide if we stay in viewer or redirect (or just fetch blob)
  useEffect(() => {
    if (!file || !file?.fileType || error) return;

    // Video with a streaming-capable token: nothing to download or decrypt here. The player uses
    // `<video src={streamUrl}>` and the browser fetches only the byte ranges it needs (206).
    // Also re-entered after a token refresh (new `file.token`), which must not restart loading.
    if (canStreamVideo(file)) {
      setDecryptedFileBlob(EMPTY_STREAM_BLOB);
      setPdfEncryptedBytes(null);
      setContentRangeLength(file.fileSize || undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fileVersionId = searchParams.get('version') || undefined;

    // MS Office files go to another route, except when opened by public link
    const isOfficeType = [
      FileTypeEnum.Wopitest,
      FileTypeEnum.Powerpoint,
      FileTypeEnum.Word,
      FileTypeEnum.Excel,
    ].includes(file?.fileType ?? FileTypeEnum.Unknown);

    const isFileScopedSession = hasFileAccessScope(authService.getToken());

    const mayRedirectToOffice =
      isOfficeType &&
      !isFileScopedSession &&
      Boolean(file?.wTkn) &&
      !fileVersionId &&
      !file?.watermark &&
      (!hasFileLimitation(file?.fileLimitations, FileLimitationEnum.DenyFileAiScraping) ||
        !file?.isReadOnly);

    if (mayRedirectToOffice && publicFileToken) {
      // Office file via public link: stay in viewer
      fetchAndDecryptBlob()
        .catch(() => {
          setError({
            status: 500,
            code: ViewerErrorType.FetchFilePermissionsFailed,
            title: t('viewer.error.fetchFileFailed.title'),
            description: t('viewer.error.fetchFileFailed.description'),
          });
        })
        .finally(() => setIsLoading(false));
      return;
    }

    if (mayRedirectToOffice) {
      // Office file, not public: redirect to Office route.
      // App webview embed always opens Office read-only (Collabora + MS + OfficeGrant).
      const token = searchParams.get('acToken') || undefined;
      const forceReadOnly = isViewerEmbedSession() || Boolean(file?.isReadOnly);
      const qs = [
        `r=${forceReadOnly ? 1 : 0}`,
        token ? `acToken=${encodeURIComponent(token)}` : '',
        publicFileToken ? `publicFileToken=${encodeURIComponent(publicFileToken)}` : '',
      ]
        .filter(Boolean)
        .join('&');
      if (file?.fileType) {
        navigate(getOfficePath(file.id, file.fileType, qs));
      }
      return;
    }

    // Default: just fetch and decrypt
    fetchAndDecryptBlob()
      .catch(() => {
        setError({
          status: 500,
          code: ViewerErrorType.FetchFilePermissionsFailed,
          title: t('viewer.error.fetchFileFailed.title'),
          description: t('viewer.error.fetchFileFailed.description'),
        });
      })
      .finally(() => setIsLoading(false));
  }, [file, file?.fileType, fetchAndDecryptBlob, error, searchParams, navigate, publicFileToken]);

  // Ref for password modal submit callbacks
  const publicPasswordModalSubmitRef = useRef<((code: string) => Promise<void>) | null>(null);
  const openPublicPasswordModalRef = useRef<() => void>(() => {});
  /** When viewing via public link, store the access code after successful unlock so public download can use it. */
  const publicAccessCodeRef = useRef<string>('');

  const applyAntivirusInfectedFromApiError = useCallback(
    (apiError: unknown) => {
      const response = (apiError as { response?: unknown }).response;
      const threatName =
        getApiErrorField(response, 'threatName') ?? getApiErrorField(response, 'threat_name');
      setError({
        status: 403,
        code: ViewerErrorType.AntivirusInfected,
        title: t('errors.antivirusFileInfected'),
        description: getAntivirusInfectedMessage(t, { threatName }),
        canReloadPage: false,
        canDownload: false,
      });
    },
    [t],
  );

  /** FILE_SIZE_VIEWER_OVER: use FileWithToken from error.response.value (viewer JWT + metadata for download UI). */
  const applyFileSizeViewerOverFromApiError = useCallback(
    (apiError: unknown) => {
      const res = (apiError as { response?: { name?: string; value?: unknown } }).response;
      const value =
        res && typeof res === 'object' && res !== null && 'value' in res
          ? (res as { value: unknown }).value
          : undefined;
      const fileFromError = mapViewerOverValueToFileWithToken(value);
      if (fileFromError) {
        setFile(fileFromError);
      }
      setError({
        status: '403',
        code: ViewerErrorType.FileSizeViewerOver,
        title: t('modal.fileSizeTooBigForOpen.title'),
        description: t('modal.fileSizeTooBigForOpen.description', {
          value: '250MB',
        }),
        canReloadPage: false,
        canDownload: fileFromError?.canDownload ?? false,
      });
    },
    [t],
  );

  /**
   * Loads file metadata for viewer:
   * - Uses public endpoint if publicFileToken is present (anonymous)
   * - Else uses authenticated endpoint
   * - Handles password modal and error states
   * @param overrideAccessCode Optional: submit password for public link
   */
  const fetchFileForViewer = useCallback(
    (overrideAccessCode?: string): Promise<void> => {
      const code = overrideAccessCode ?? undefined;

      if (publicFileToken) {
        // Use public API for public links
        return publicFileService
          .getPublicFileForViewer(publicFileToken, Number(fileId), code || undefined)
          .then((response) => {
            normalizeAndSetFile(response);
            // Keep access code in sync for downloadPublicFile (cached password or modal submit)
            if (code !== undefined) {
              publicAccessCodeRef.current = code;
            }
          })
          .catch((error) => {
            // If 401, show password modal
            if (error?.status === 401) {
              openPublicPasswordModalRef.current?.();
              throw error;
            }

            const publicErrCode = getApiErrorCode(error?.response);
            if (publicErrCode === ApiErrorEnum.FileSizeViewerOver) {
              applyFileSizeViewerOverFromApiError(error);
              return;
            }
            if (publicErrCode === ApiErrorEnum.AntivirusFileInfected) {
              applyAntivirusInfectedFromApiError(error);
              return;
            }

            const mapped = mapPublicLinkHttpError(error);
            if (mapped.kind === 'unavailable') {
              setError({
                status: 404,
                code: ViewerErrorType.PublicLinkUnavailable,
                title: t('errors.publicLinkNotFoundTitle'),
                description: t('errors.publicLinkNotFoundMessage'),
                canReloadPage: false,
                canDownload: false,
              });
              return;
            }
            if (mapped.kind === 'itemNotFound') {
              setError({
                status: 404,
                code: ViewerErrorType.PublicItemUnavailable,
                title: t('errors.publicLinkItemNotAvailable'),
                description: '',
                canReloadPage: false,
                canDownload: false,
              });
              return;
            }
            if (mapped.kind === 'expired') {
              setError({
                status: 403,
                code: ViewerErrorType.PublicLinkExpired,
                title: mapped.maxAccess
                  ? t('errors.publicLinkMaxAccessTitle')
                  : t('errors.publicLinkExpiredTitle'),
                description: mapped.maxAccess
                  ? t('errors.publicLinkMaxAccessMessage')
                  : t('errors.publicLinkExpiredMessage'),
                canReloadPage: false,
                canDownload: false,
              });
              return;
            }

            notification.error({ title: t('errors.genericError') });
          });
      }

      // Otherwise, use authenticated fetch
      const token = searchParams.get('acToken') || undefined;
      const fileVersionId = searchParams.get('version') || undefined;
      return fileService
        .getFileForViewer(Number(fileId), {
          acToken: token,
          vid: fileVersionId ? Number(fileVersionId) : undefined,
          // This client can stream videos with HTTP Range (see videoStreaming.ts)
          stream: true,
        })
        .then((response) => {
          normalizeAndSetFile(response);
        })
        .catch((error) => {
          // Special handling for folder password request
          if (
            error?.response === ApiErrorEnum.FolderPasswordRequired ||
            error?.response?.name === ApiErrorEnum.FolderPasswordRequired
          ) {
            throw error;
          }

          const errCode = getApiErrorCode(error?.response);

          if (errCode === ApiErrorEnum.FileSizeViewerOver) {
            applyFileSizeViewerOverFromApiError(error);
            return;
          }

          if (errCode === ApiErrorEnum.AntivirusFileInfected) {
            applyAntivirusInfectedFromApiError(error);
            return;
          }

          switch (errCode) {
            case ApiErrorEnum.AntiCaptureActivated:
              navigate(`/file/protected/${fileId}`);
              break;
            case ApiErrorEnum.DownloadRestrictionActivated:
              notification.error({
                title: t('errors.downloadRestrictionActivated'),
              });
              navigate(`/file/protected/${fileId}`);
              break;
            case ApiErrorEnum.ObjectNotFound:
              setError({
                status: 404,
                code: ViewerErrorType.NoPermissions,
                title: t('errors.fileNotAvailable.title'),
                description: t('errors.fileNotAvailable.description'),
              });
              notification.error({
                title: t('errors.fileNotAvailable.title'),
              });
              break;
            default:
              setError({
                status: 403,
                code: ViewerErrorType.NoPermissions,
                title: t('viewer.error.noPermissions.title'),
                description: t('viewer.error.noPermissions.description'),
              });
              notification.error({
                title: t('errors.hitASnag'),
              });
          }
        });
    },
    [
      fileId,
      searchParams,
      publicFileToken,
      t,
      notification,
      applyFileSizeViewerOverFromApiError,
      applyAntivirusInfectedFromApiError,
    ],
  );

  // Show the password modal for public links
  const openPublicPasswordModal = useCallback(() => {
    const modalInstance = modal.confirm({
      title: (
        <span className="flex items-center gap-2">
          <Icon name="LockIcon" className="h-5 w-5" />
          {t('common.protectedContentTitle')}
        </span>
      ),
      content: (
        <FilePublicPasswordForm
          error={null}
          loading={false}
          compact
          onSubmit={(code) => publicPasswordModalSubmitRef.current?.(code)}
        />
      ),
      width: 500,
      footer: null,
      closable: false,
      mask: { closable: false },
    });
    publicPasswordModalSubmitRef.current = async (code: string) => {
      try {
        await fetchFileForViewer(code);
        if (publicFileToken) setPublicFilePassword(publicFileToken, code);
        publicAccessCodeRef.current = code;
        modalInstance.destroy();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ?? err?.response?.message ?? t('errors.invalidAccessCode');
        modalInstance.update({
          content: (
            <FilePublicPasswordForm
              error={msg}
              loading={false}
              compact
              onSubmit={(c) => publicPasswordModalSubmitRef.current?.(c)}
            />
          ),
        });
      }
    };
  }, [modal, t, fetchFileForViewer, publicFileToken, setPublicFilePassword]);

  // Put latest modal opener on ref; enables re-use/callbacks
  openPublicPasswordModalRef.current = openPublicPasswordModal;

  // Converts file type/extension to canonical supported type
  const normalizeAndSetFile = useCallback(
    (response: FileWithToken) => {
      const normalizedFile = normalizeViewerFile(response);
      setFile(normalizedFile);

      // If no extension, treat as folder
      if (response.fileExtension === '') {
        setError({
          status: 500,
          code: ViewerErrorType.IsFolder,
          title: t('viewer.error.isFolder.title'),
          description: t('viewer.error.isFolder.description'),
          canReloadPage: false,
        });
      } else if (!isSupportedFileType(normalizedFile.fileType)) {
        setError({
          status: 404,
          code: ViewerErrorType.OfficeNoPreview,
          title: t('viewer.error.officeNoPreview.title'),
          description: t('viewer.error.officeNoPreview.description'),
          canReloadPage: false,
        });
      }
    },
    [t],
  );

  // Load (on mount, or when fileId changes — e.g. switching notes in-place): get file metadata,
  // authenticate, show errors. Refetching by fileId avoids remounting the whole viewer.
  useEffect(() => {
    if (lastFetchedFileIdRef.current === fileId) return;

    // Webview embed: wait for AuthProvider QS seed (`token`+`rToken` / viewerEmbed) before fetch
    // so getFileForViewer does not race and 401.
    const embedFromQs = Boolean(searchParams.get('token')) && Boolean(searchParams.get('rToken'));
    const needsEmbedAuthGate = !publicFileToken && (isViewerEmbedSession() || embedFromQs);
    if (needsEmbedAuthGate && (!customTokens?.token || !isLoggedIn)) {
      setIsLoading(true);
      return;
    }

    const isFileSwitch = lastFetchedFileIdRef.current !== undefined;
    lastFetchedFileIdRef.current = fileId;

    // Switching to a different file: clear the previous file's state so its content and
    // edit mode don't leak into the new file while it loads.
    if (isFileSwitch) {
      setFile(undefined);
      setDecryptedFileBlob(null);
      setPdfEncryptedBytes(null);
      setImageBlobOverride(null);
      setPdfBlobOverride(null);
      setPdfInitialPage(undefined);
      setError(null);
      setIsInEditMode(false);
      setIsDirty(false);
    }

    setIsLoading(true);

    const storedPassword = publicFileToken ? getPublicFilePassword(publicFileToken) : null;
    fetchFileForViewer(storedPassword ?? undefined)
      .catch((error) => {
        // Password modal is opened by fetchFileForViewer for 401. Don't show it here.
        if (publicFileToken && error?.status === 401) return;
        // Test for folder password error in two backend formats
        const isFolderPasswordRequired =
          (error?.status === 403 &&
            error?.response?.name === ApiErrorEnum.FolderPasswordRequired) ||
          (error?.status === 400 && error?.response === ApiErrorEnum.FolderPasswordRequired);
        if (isFolderPasswordRequired) {
          // Backend sometimes sends plain string instead of error object. Synthesize full error if needed.
          const syntheticError =
            error?.status === 403 && error?.response && typeof error.response === 'object'
              ? error
              : {
                  status: 403,
                  response: {
                    name: ApiErrorEnum.FolderPasswordRequired,
                    value: {
                      errorCode: ApiErrorEnum.PasswordRequired,
                      folderId: error?.response?.value?.folderId ?? Number(fileId),
                      folderName: error?.response?.value?.folderName ?? 'Folder',
                    },
                  },
                  config: error?.config,
                };
          // If handled by global handler, mark pending so viewer can retry password
          if (handleFilePasswordError(syntheticError)) {
            setPendingFilePasswordSource('viewer');
            viewerCausedPendingRef.current = true;
          }
          return;
        }

        // Various errors: protected routing, restrictions, not found, or generic error
        if (error?.status === 400) {
          switch (error.response) {
            case ApiErrorEnum.AntiCaptureActivated:
              navigate(`/file/protected/${fileId}`);
              break;
            case ApiErrorEnum.DownloadRestrictionActivated:
              notification.error({
                title: t('errors.downloadRestrictionActivated'),
              });
              navigate(`/file/protected/${fileId}`);
              break;
            case ApiErrorEnum.ObjectNotFound:
              setError({
                status: 404,
                code: ViewerErrorType.NoPermissions,
                title: t('errors.fileNotAvailable.title'),
                description: t('errors.fileNotAvailable.description'),
              });
              notification.error({
                title: t('errors.fileNotAvailable.title'),
              });
              break;
            default:
              setError({
                status: 403,
                code: ViewerErrorType.NoPermissions,
                title: t('viewer.error.noPermissions.title'),
                description: t('viewer.error.noPermissions.description'),
              });
              notification.error({
                title: t('errors.hitASnag'),
              });
          }
        } else {
          setError({
            status: 500,
            code: ViewerErrorType.FetchFilePermissionsFailed,
            title: t('viewer.error.fetchFilePermissionsFailed.title'),
            description: t('viewer.error.fetchFilePermissionsFailed.description'),
          });
          notification.error({
            title: t('errors.hitASnag'),
          });
        }
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fileId,
    searchParams,
    publicFileToken,
    navigate,
    notification,
    t,
    fetchFileForViewer,
    setPendingFilePasswordSource,
    getPublicFilePassword,
    customTokens?.token,
    isLoggedIn,
  ]);

  // If the password pending state gets cleared and it was set by this viewer, re-fetch file (unless user cancelled)
  const pendingFilePasswordRequest = useAppStore((s) => s.pendingFilePasswordRequest);
  const setPendingFilePasswordCancelledByUser = useAppStore(
    (s) => s.setPendingFilePasswordCancelledByUser,
  );
  useEffect(() => {
    if (pendingFilePasswordRequest) {
      hadPendingRequestRef.current = true;
    } else if (hadPendingRequestRef.current) {
      hadPendingRequestRef.current = false;
      if (viewerCausedPendingRef.current) {
        viewerCausedPendingRef.current = false;
        const cancelledByUser = useAppStore.getState().pendingFilePasswordCancelledByUser;
        if (cancelledByUser) {
          setPendingFilePasswordCancelledByUser(false);
          return;
        }
        setIsLoading(true);
        fetchFileForViewer()
          .catch((e) => {
            // Redo folder password check logic for retry
            const isFolderPasswordRequired =
              (e?.status === 403 && e?.response?.name === ApiErrorEnum.FolderPasswordRequired) ||
              (e?.status === 400 && e?.response === ApiErrorEnum.FolderPasswordRequired);
            if (isFolderPasswordRequired) {
              const syntheticError =
                e?.status === 403 && e?.response && typeof e.response === 'object'
                  ? e
                  : {
                      status: 403,
                      response: {
                        name: ApiErrorEnum.FolderPasswordRequired,
                        value: {
                          errorCode: ApiErrorEnum.PasswordRequired,
                          folderId: e?.response?.value?.folderId ?? Number(fileId),
                          folderName: e?.response?.value?.folderName ?? 'Folder',
                        },
                      },
                      config: e?.config,
                    };
              if (handleFilePasswordError(syntheticError)) {
                setPendingFilePasswordSource('viewer');
                viewerCausedPendingRef.current = true;
              }
              return;
            }
            setError({
              status: 500,
              code: ViewerErrorType.FetchFilePermissionsFailed,
              title: t('viewer.error.fetchFilePermissionsFailed.title'),
              description: t('viewer.error.fetchFilePermissionsFailed.description'),
            });
            notification.error({ title: t('errors.hitASnag') });
          })
          .finally(() => setIsLoading(false));
      }
    }
  }, [
    pendingFilePasswordRequest,
    fetchFileForViewer,
    setPendingFilePasswordSource,
    setPendingFilePasswordCancelledByUser,
    fileId,
    t,
    notification,
  ]);

  /**
   * Enter edit mode or redirect Office docs to native view
   */
  const handleEditFile = useCallback(() => {
    if (isOfficeFile && file?.fileType && fileId) {
      const qs = isViewerEmbedSession() ? 'r=1' : undefined;
      navigate(getOfficePath(fileId, file.fileType, qs));
    } else {
      setIsInEditMode(true);
      message.info(t('common.editModeEnabled'));
    }
  }, [isOfficeFile, file?.fileType, fileId, navigate, message, t]);

  /**
   * Exit edit mode, reset flag/UI
   */
  const handleExitEditMode = useCallback(() => {
    void (async () => {
      if (isDirty) {
        const canLeave = await requestLeave();
        if (!canLeave) {
          return;
        }
      }

      message.info(isSignMode ? t('viewer.sign.modeDisabled') : t('common.editModeDisabled'));
      setIsInEditMode(false);
      setIsSignMode(false);
      setIsDirty(false);
    })();
  }, [isDirty, isSignMode, message, requestLeave, t]);

  /**
   * Save the file
   * Just toggles mode off - actual save is handled in referenced component
   */
  const handleSaveFile = useCallback(() => {
    setIsInEditMode(false);
    setIsSignMode(false);
  }, []);

  /**
   * Save the file
   */
  const handleSave = useCallback(async () => {
    if (isSignMode && pdfSignRef.current) {
      await pdfSignRef.current.save();
      return;
    }
    if (
      (file?.fileType === FileTypeEnum.Text ||
        file?.fileType === FileTypeEnum.Code ||
        file?.fileType === FileTypeEnum.Note) &&
      textViewerRef.current
    ) {
      await textViewerRef.current.save();
    } else if (file?.fileType === FileTypeEnum.Image && imageEditorRef.current) {
      await imageEditorRef.current.save();
    }
  }, [file?.fileType, isSignMode]);

  const handleStartSign = useCallback(() => {
    setIsSignMode(true);
    setIsDirty(false);
    message.info(t('viewer.sign.modeEnabled'));
  }, [message, t]);

  const handleSignUnavailable = useCallback(() => {
    setIsSignMode(false);
    setIsDirty(false);
  }, []);

  const handleSignSaved = useCallback(
    (
      signedBlob?: Blob,
      restorePage?: number,
      restoreScroll?: { scrollTop: number; scrollLeft: number },
    ) => {
      if (signedBlob) {
        setPdfBlobOverride(signedBlob);
        setPdfInitialPage(restorePage && restorePage > 0 ? restorePage : 1);
        setPdfInitialScroll(restoreScroll);
        setPdfViewEpoch((n) => n + 1);
      }
      setIsSignMode(false);
      setIsDirty(false);
    },
    [],
  );

  /**
   * Receives saved image blob from image editor, sets as in-memory override, exits edit mode
   */
  const handleImageEditorSave = useCallback(
    (newBlob: Blob) => {
      setImageBlobOverride(newBlob);
      setIsDirty(false);
      handleExitEditMode();
    },
    [handleExitEditMode],
  );

  /**
   * Is file editable? (Not read-only, is a valid type, not SVG)
   * @returns true if the file is editable, false otherwise
   */
  const canEditFile = useMemo(() => {
    if (hasFileAccessScope(authService.getToken())) {
      return false;
    }
    return (
      !file?.isReadOnly &&
      [
        FileTypeEnum.Image,
        FileTypeEnum.Text,
        FileTypeEnum.Code,
        FileTypeEnum.Note,
        FileTypeEnum.Word,
        FileTypeEnum.Excel,
        FileTypeEnum.Powerpoint,
      ].includes(file?.fileType ?? FileTypeEnum.Unknown) &&
      file?.fileExtension !== '.svg'
    );
  }, [file, file?.fileType]);

  /**
   * Notes open straight into edit mode (once per file) instead of the read-only preview.
   */
  const autoEditNoteFileIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (file?.fileType !== FileTypeEnum.Note || !canEditFile) return;
    if (autoEditNoteFileIdRef.current === file.id) return;
    autoEditNoteFileIdRef.current = file.id;
    setIsInEditMode(true);
  }, [file?.fileType, file?.id, canEditFile]);

  /**
   * Can this file be shared? (Checks denial limitation)
   * @returns true if the file can be shared, false otherwise
   */
  const canShareFile = useMemo(() => {
    if (!file || hasFileAccessScope(authService.getToken())) return false;
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileShare);
  }, [file]);

  /** Move toolbar visibility: matches Organize → Move in fileActions (no DenyFileMove). */
  const canMoveFile = useMemo(() => {
    if (!file || hasFileAccessScope(authService.getToken())) return false;
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileMove);
  }, [file]);

  /**
   * Is downloading allowed for this file?
   * @returns true if the file can be downloaded, false otherwise
   */
  const canDownloadFile = useMemo(() => {
    return file?.canDownload;
  }, [file]);

  /**
   * Is anti-scraping enabled? (AI scraping prevention)
   * @returns true if the file is anti-scraping enabled, false otherwise
   */
  const isAntiScrapingActive = useMemo(() => {
    return (
      file?.isReadOnly &&
      hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileAiScraping)
    );
  }, [file]);

  /**
   * PDF image-sign: writable PDF only (not anti-scraping / protected path).
   */
  const canSignFile = useMemo(() => {
    if (hasFileAccessScope(authService.getToken())) return false;
    return (
      !file?.isReadOnly &&
      (file?.fileExtension || '').toLowerCase() === '.pdf' &&
      !isAntiScrapingActive
    );
  }, [file?.isReadOnly, file?.fileExtension, isAntiScrapingActive]);

  /**
   * Converts loaded fileWithToken into File API format for menus, download, etc.
   * @returns the parsed file
   */
  const parsedFile: File | null = useMemo(() => {
    if (!file) return null;
    return {
      id: file.id,
      cufi: file.cufi,
      fileName: file.fileName,
      fileExtension: file.fileExtension,
      fileSize: file.fileSize,
      isReadOnly: file.isReadOnly,
      fileType: file.fileType,
      fileLimitations: file.fileLimitations,
      thumbnail: '',
      isFavourite: false,
      isPublicShare: false,
      isArchived: false,
      isDeleted: false,
      isShared: false,
      isUnRead: false,
      isQuickShare: false,
      isAiGrant: false,
      isShortcut: false,
      isPasswordSet: false,
      isPasswordBypass: false,
      hasShortcuts: false,
      lastModifiedAt: 0,
      isFolder: false,
      ownerId: 0,
      tags: [],
      salesStatus: FileSalesStatusEnum.None,
      aiOperationStatus: AiClassificationStatusEnum.None,
      specialFolder: SpecialFolderTypeEnum.None,
    };
  }, [file]);

  /**
   * Download action menu based on file context/permissions
   * @returns the download actions
   */
  const getFilePasswordSession = useAppStore((s) => s.getFilePasswordSession);

  /**
   * Download actions
   * @returns the download actions
   */
  const downloadActions = useMemo<ViewerDownloadAction[]>(() => {
    if (!parsedFile) {
      return [];
    }

    const downloadSubmenuKey = 'downloadSubmenu' as FileMenuActionEnum;
    const downloadDefinition = fileActions.find((action) => action.key === downloadSubmenuKey);

    if (!downloadDefinition || !downloadDefinition.children) {
      return [];
    }

    // Build context for action condition testing
    const context: FileActionContext = {
      files: [parsedFile],
      authenticatedUser,
      isInFileRoute,
      isAdminOrContributor,
      isReader,
      isCurrentFolderReadOnly: parsedFile.isReadOnly ?? false,
      tags: [],
      isRightPanelOpen,
      isUploadDisabledInCurrentFolder: false,
      getFilePasswordSession,
    };

    // Include only real download actions (not context-menu-only, etc)
    // When viewing via public link, only show Download (no DownloadEncrypted); use public download endpoint
    return downloadDefinition.children
      .filter((action) => {
        if (publicFileToken && action.key === FileMenuActionEnum.DownloadEncrypted) {
          return false;
        }
        const isSupportedAction =
          action.key === FileMenuActionEnum.Download ||
          action.key === FileMenuActionEnum.DownloadEncrypted;

        if (!isSupportedAction || action.onlyContextMenu) {
          return false;
        }

        return action.condition ? action.condition(context) : true;
      })
      .map((action) => {
        const translatedLabel =
          typeof action.label === 'string' ? t(action.label) : (action.label ?? '');
        const labelText = typeof action.label === 'string' ? t(action.label) : '';

        return {
          key: action.key as FileMenuActionEnum,
          label: translatedLabel,
          labelText,
          icon: action.icon,
        };
      });
  }, [
    parsedFile,
    authenticatedUser,
    isInFileRoute,
    isAdminOrContributor,
    isReader,
    isRightPanelOpen,
    getFilePasswordSession,
    publicFileToken,
    t,
  ]);

  // Items for Antd <Dropdown /> for download (built from actions above)
  const downloadMenuItems = useMemo<MenuProps['items']>(() => {
    if (!downloadActions.length) {
      return [];
    }
    return downloadActions.map((action) => ({
      key: action.key,
      label: (
        <span className="flex items-center gap-2">
          {action.icon}
          <span>{action.label}</span>
        </span>
      ),
    }));
  }, [downloadActions]);

  // Show download menu if allowed, file loaded, and at least one item
  const showDownloadActions = Boolean(canDownloadFile && parsedFile && downloadActions.length > 0);

  // Executes download: public endpoint when publicFileToken, otherwise authenticated download/encrypted
  const handleDownloadAction = useCallback(
    async (actionKey: FileMenuActionEnum) => {
      if (!parsedFile) return;

      if (actionKey === FileMenuActionEnum.Download) {
        if (publicFileToken) {
          try {
            const { blob, headers } = await publicFileService.downloadPublicFile(
              publicFileToken,
              parsedFile.id,
              publicAccessCodeRef.current || undefined,
            );
            const fileName = getPublicDownloadFilename(headers, parsedFile.fileName ?? 'download');

            const contentSha256 = getResponseHeader(headers, 'x-content-sha256');
            if (contentSha256) {
              const calculatedHash = await getHashFromBytes(await blob.arrayBuffer(), false);
              if (contentSha256.trim().toLowerCase() !== calculatedHash.trim().toLowerCase()) {
                notification.error({
                  title: t('errors.fileIntegrityCheckFailed') || 'File integrity check failed',
                  description:
                    t('errors.fileIntegrityCheckFailedDescription') ||
                    'The downloaded file may be corrupted. Please try downloading again.',
                  duration: 0,
                });
                return;
              }
            }

            triggerBlobDownload(blob, fileName);
          } catch (error: unknown) {
            const errCode = getApiErrorCode((error as { response?: unknown })?.response);
            if (errCode === ApiErrorEnum.AntivirusFileInfected) {
              const response = (error as { response?: unknown }).response;
              const threatName =
                getApiErrorField(response, 'threatName') ??
                getApiErrorField(response, 'threat_name');
              notification.error({
                title: getAntivirusInfectedMessage(t, {
                  threatName,
                  fileName: parsedFile.fileName,
                }),
              });
              return;
            }

            const mapped = mapPublicLinkHttpError(error);
            if (mapped.kind === 'watermarkDenied') {
              // Keep viewer open; only show protected message
              notification.error({
                title: t('errors.watermarkProtectionDownloadDenied'),
              });
              return;
            }
            if (mapped.kind === 'unavailable') {
              setError({
                status: 404,
                code: ViewerErrorType.PublicLinkUnavailable,
                title: t('errors.publicLinkNotFoundTitle'),
                description: t('errors.publicLinkNotFoundMessage'),
                canReloadPage: false,
                canDownload: false,
              });
              return;
            }
            if (mapped.kind === 'itemNotFound') {
              setError({
                status: 404,
                code: ViewerErrorType.PublicItemUnavailable,
                title: t('errors.publicLinkItemNotAvailable'),
                description: '',
                canReloadPage: false,
                canDownload: false,
              });
              return;
            }
            if (mapped.kind === 'expired') {
              setError({
                status: 403,
                code: ViewerErrorType.PublicLinkExpired,
                title: mapped.maxAccess
                  ? t('errors.publicLinkMaxAccessTitle')
                  : t('errors.publicLinkExpiredTitle'),
                description: mapped.maxAccess
                  ? t('errors.publicLinkMaxAccessMessage')
                  : t('errors.publicLinkExpiredMessage'),
                canReloadPage: false,
                canDownload: false,
              });
              return;
            }

            notification.error({ title: t('errors.genericError') });
          }
          return;
        }
        handleDownloadFiles([parsedFile]);
        return;
      }
      if (actionKey === FileMenuActionEnum.DownloadEncrypted) {
        handleDownloadEncryptedFile(parsedFile);
      }
    },
    [
      handleDownloadEncryptedFile,
      handleDownloadFiles,
      parsedFile,
      publicFileToken,
      notification,
      t,
    ],
  );

  // antd Menu "onClick" handler for download
  const handleDownloadMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      handleDownloadAction(key as FileMenuActionEnum);
    },
    [handleDownloadAction],
  );

  // Compose "viewer file" to pass to preview components
  const viewerFile: ViewerFile | null = useMemo(() => {
    if (!file || !decryptedFileBlob) return null;
    return {
      id: file.id,
      cufi: file.cufi,
      fileName: file.fileName,
      fileExtension: file.fileExtension,
      fileType: file?.fileType ?? FileTypeEnum.Unknown,
      expirationAt: file.expirationAt,
      token: file.token,
      fileSize: file.fileSize,
      isReadOnly: file.isReadOnly,
      canDownload: file.canDownload,
      fileLimitations: file.fileLimitations,
      decryptedFileBlob: decryptedFileBlob,
      encryptedFileBytes: pdfEncryptedBytes,
      // Video streaming: GET URL + token expiry, only when the token allows it
      ...(canStreamVideo(file)
        ? {
            streamUrl: functionService.getFileStreamUrl(file.token),
            streamExpiresAt: getJwtExpiration(file.token),
          }
        : {}),
    };
  }, [file, decryptedFileBlob, pdfEncryptedBytes, functionService]);

  // AI action items: document chat and summary (shown based on limitations)
  const aiFileActionsItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];
    if (!file || !parsedFile) return items;

    if (!hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileAIChat)) {
      items.push({
        key: 'ai-chat',
        label: t('common.artificialIntelligenceDocumentChat'),
        icon: <Icon name="BotMessageSquareIcon" className="h-5 w-5" />,
        onClick: () => {
          setRightPanelKind('aiDocumentChat');
          setRightPanelContent(<AiDocumentChat file={parsedFile} />);
          openRightPanel();
        },
      });
    }
    if (!hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileAISummary)) {
      items.push({
        key: 'ai-summary',
        label: t('common.artificialIntelligenceSummary'),
        icon: <Icon name="ScanTextIcon" className="h-5 w-5" />,
        onClick: () => {
          // Kind first: FilesHeader sync treats default 'detail' as FileDetail and overwrites.
          setRightPanelKind('aiSummary');
          setRightPanelContent(
            <AiSummary
              file={file}
              onSummaryGenerated={() => {}}
              onRequestClose={closeRightPanel}
            />,
          );
          openRightPanel();
        },
      });
    }
    return items;
  }, [
    file,
    t,
    parsedFile,
    openRightPanel,
    closeRightPanel,
    setRightPanelContent,
    setRightPanelKind,
  ]);

  // --- Reset effects for switching files/modes ---

  // Clear in-memory overrides when changing files
  useEffect(() => {
    setImageBlobOverride(null);
    setPdfBlobOverride(null);
    setPdfInitialPage(undefined);
  }, [file?.id]);

  // Share modal should open only once per file
  useEffect(() => {
    shareModalOpenedRef.current = false;
  }, [fileId]);

  // Handle ?action=share in the URL (on load)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'share' && !shareModalOpenedRef.current && canShareFile && parsedFile) {
      shareModalOpenedRef.current = true;
      handleShareFiles([parsedFile]);
    }
  }, [searchParams, canShareFile, parsedFile, handleShareFiles]);

  // Location-scoped tab title (DocumentTitleSync is the sole document.title writer)
  useRoutePageTitle({
    isLoading,
    isError: !!error,
    label: file?.fileName || (error || !isLoading ? t('common.untitled') : undefined),
  });

  /**
   * Check if the file is an HTML file based on extension
   * @returns true if the file is an HTML file, false otherwise
   */
  const isHtmlFile = useMemo(() => {
    const ext = (file?.fileExtension || '').toLowerCase().replace(/^\./, '');
    return ext === 'html' || ext === 'htm';
  }, [file?.fileExtension]);

  /**
   * Check if the file is a PDF file based on extension
   * @returns true if the file is a PDF file, false otherwise
   */
  const isPdfFile = useMemo(() => {
    const ext = (file?.fileExtension || '').toLowerCase().replace(/^\./, '');
    return ext === 'pdf';
  }, [file?.fileExtension]);

  /**
   * Handle the click event for the HTML preview button
   */
  const handleHtmlPreviewClick = useCallback(() => {
    const currentlyEnabled = textViewerRef.current?.isHtmlPreviewEnabled?.() ?? false;
    if (currentlyEnabled) {
      textViewerRef.current?.toggleHtmlPreview?.();
      setIsHtmlPreviewEnabled(false);
    } else {
      modal.confirm({
        title: t('viewer.htmlPreview.title'),
        content: t('viewer.htmlPreview.description'),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        onOk: () => {
          textViewerRef.current?.enableHtmlPreview?.();
          setIsHtmlPreviewEnabled(true);
        },
      });
    }
  }, [modal, t]);

  useEffect(() => {
    if (isHtmlFile && textViewerRef.current) {
      const enabled = textViewerRef.current.isHtmlPreviewEnabled?.() ?? false;
      setIsHtmlPreviewEnabled(enabled);
    }
  }, [isHtmlFile, file?.id]);

  /**
   * Video streaming: asks the API for a fresh viewer token (same params as the initial load).
   * The new token flows into `viewerFile.streamUrl`, and the player swaps the source keeping the
   * playback position. Errors are already surfaced by fetchFileForViewer.
   */
  const refreshViewerToken = useCallback(async () => {
    try {
      await fetchFileForViewer();
    } catch {
      // Handled inside fetchFileForViewer (notification / password modal)
    }
  }, [fetchFileForViewer]);

  // API: What this hook exposes to consuming view or component
  return {
    fileId,
    file,
    isLoading,
    refreshViewerToken,
    fileAccessExpiration,
    isOfficeFile,
    error,
    decryptedFileBlob,
    contentRangeLength,
    pdfEncryptedBytes,
    canEditFile,
    canSignFile,
    canDownloadFile,
    downloadActions,
    downloadMenuItems,
    showDownloadActions,
    handleDownloadAction,
    handleDownloadMenuClick,
    isInEditMode,
    isSignMode,
    handleEditFile,
    handleStartSign,
    handleExitEditMode,
    handleSaveFile,
    isAntiScrapingActive,
    viewerFile,
    // Editor state and refs
    isDirty,
    imageBlobOverride,
    pdfBlobOverride,
    pdfViewEpoch,
    pdfInitialPage,
    pdfInitialScroll,
    textViewerRef,
    imageEditorRef,
    pdfSignRef,
    handleSave,
    handleImageEditorSave,
    handleSignUnavailable,
    handleSignSaved,
    setIsDirty,
    aiFileActionsItems,
    parsedFile,
    canShareFile,
    canMoveFile,
    publicFileToken,
    hasAcToken,
    isHtmlFile,
    isPdfFile,
    isHtmlPreviewEnabled,
    handleHtmlPreviewClick,
    isNoteContext,
  };
}

export default useViewer;
